import { useTranslation } from "react-i18next";
import { BasketBillingType, BasketForm } from "../BasketService";
import { NaviButtons } from "../components/NaviButtons";
import { UserDisplay } from "../components/UserDisplay";
import { isNotEmptyString, numberSequence, withExceptionCapturing } from "../common";
import { SubmitHandler, useForm } from "react-hook-form";

interface Inputs {
	acceptTerms: boolean;
}

export const StepSummary = ({
	returnUrl,
	basketForm,
	onBack,
	onNext
}: {
	returnUrl: string;
	basketForm: BasketForm;
	onBack: () => void;
	onNext: () => Promise<void>;
}) => {
	const { t } = useTranslation();

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting }
	} = useForm<Inputs>({
		defaultValues: {
			acceptTerms: false
		},
		mode: "onTouched"
	});

	const onSubmit: SubmitHandler<Inputs> = () => onNext();
	const acceptTermsError = errors.acceptTerms?.message;

	return (
		<form onSubmit={withExceptionCapturing(handleSubmit(onSubmit))}>
			<div className="row space-t">
				<div className="col-md-12">
					<h3>{t("summaryTitle")}</h3>
				</div>
				<div className="col-md-12">
					<div className="row space-t">
						<div className="col-md-12">
							<h4>{t("companyBasicInformationTitle")}</h4>
							<div className="row">
								<div className="col-md-12">
									<div className="row">
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("companyName")}</p>
											<p className="summary-value">{basketForm.companyName}</p>
										</div>
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("companyId")}</p>
											<p className="summary-value">{basketForm.businessId}</p>
										</div>
									</div>
									<div className="row">
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("streetAddress")}</p>
											<p className="summary-value">{basketForm.streetAddress}</p>
										</div>
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("postalCode")}</p>
											<p className="summary-value">{basketForm.postalCode}</p>
										</div>
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("city")}</p>
											<p className="summary-value">{basketForm.city}</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="row space-t">
						<div className="col-md-12">
							<h4>{t("subscriberInfo")}</h4>
							<div className="row">
								<div className="col-md-12">
									<div className="row">
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("lastName")}</p>
											<p className="summary-value">{basketForm.lastName}</p>
										</div>
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("firstName")}</p>
											<p className="summary-value">{basketForm.firstName}</p>
										</div>
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("phone")}</p>
											<p className="summary-value">{basketForm.phone}</p>
										</div>

										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("email")}</p>
											<p className="summary-value">{basketForm.email}</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="row space-t">
						<div className="col-md-12">
							<h4>{t("billingInfoTitle")}</h4>
							<div className="row">
								<div className="col-md-12">
									<div className="row">
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("billingMethod")}</p>
											<p className="summary-value">{t(`billingType${basketForm.billingType}`)}</p>
										</div>
										{basketForm.billingType === BasketBillingType.Online && (
											<div className="col-md-3 col-sm-12 col-xs-12">
												<p className="summary-label">{t("onlineBillingAddress")}</p>
												<p className="summary-value">{basketForm.onlineBillingAddress}</p>
											</div>
										)}
										{basketForm.billingType === BasketBillingType.Email && (
											<div className="col-md-3 col-sm-12 col-xs-12">
												<p className="summary-label">{t("email")}</p>
												<p className="summary-value">{basketForm.billingEmail}</p>
											</div>
										)}
									</div>
									<div className="row">
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("billingLanguage")}</p>
											<p className="summary-value">{t(`billingLanguage${basketForm.billingLanguage}`)}</p>
										</div>
									</div>
									<div className="row">
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("billingStreetAddress")}</p>
											<p className="summary-value">{basketForm.billingStreetAddress}</p>
										</div>
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("postalCode")}</p>
											<p className="summary-value">{basketForm.billingPostalCode}</p>
										</div>
										<div className="col-md-3 col-sm-12 col-xs-12">
											<p className="summary-label">{t("city")}</p>
											<p className="summary-value">{basketForm.billingCity}</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="row space-t">
						<div className="col-md-12">
							<div className="row selected-service-package">
								<div className="col-md-3 col-sm-12">
									<h4>{t("chosenPackage")}</h4>
									<div className="price">
										<h4>
											{t(`type${basketForm.basketType}`)} {t(`price${basketForm.basketType}`)} €
										</h4>
										<span> / {t("month")}</span>
									</div>
								</div>
								<div className="col-md-3 col-sm-12">
									<h4>{t("contractContentTitle")}</h4>
									<h5>{t("maxUsageCountTitle")}</h5>
									<div>{t(`maxUsageCount${basketForm.basketType}`)}</div>
									<h5>{t("subscriptionPeriodTitle")}</h5>
									<div>{t("subscriptionPeriodContent")}</div>
									<p>{t("contractContent1")}</p>
									<p>{t("contractContent2")}</p>
									<p>{t("contractContent3")}</p>
								</div>
								<div className="col-md-3 col-sm-12">
									<h4>{t("servicePackageContentTitle")}</h4>
									<ul>
										{numberSequence(1, 8).map((i) => <li>{t(`servicePackageContent${i}`)}</li>)}
									</ul>
								</div>
							</div>
						</div>
					</div>
					<div className="row space-t">
						<div className="col-md-12">
							<h4>{t("usersTitle")}</h4>
							{basketForm.userAccountForSubscriber && (
								<UserDisplay lastName={basketForm.lastName} firstName={basketForm.firstName} email={basketForm.email} />
							)}
							{isNotEmptyString(basketForm.user1LastName) && (
								<UserDisplay
									lastName={basketForm.user1LastName}
									firstName={basketForm.user1FirstName}
									email={basketForm.user1Email}
								/>
							)}
							{isNotEmptyString(basketForm.user2LastName) && (
								<UserDisplay
									lastName={basketForm.user2LastName}
									firstName={basketForm.user2FirstName}
									email={basketForm.user2Email}
								/>
							)}
							{isNotEmptyString(basketForm.user3LastName) && (
								<UserDisplay
									lastName={basketForm.user3LastName}
									firstName={basketForm.user3FirstName}
									email={basketForm.user3Email}
								/>
							)}
							{isNotEmptyString(basketForm.user4LastName) && (
								<UserDisplay
									lastName={basketForm.user4LastName}
									firstName={basketForm.user4FirstName}
									email={basketForm.user4Email}
								/>
							)}
						</div>
					</div>
					<div className="row space-t">
						<div className="col-md-12">
							<h4>{t("termsTitle")}</h4>
							<div className="row">
								<div className="col-md-12">
									<div className="row">
										<div className="col-md-12">
											<a href={basketForm.termsUrl} target="_blank">
												{t("termsText")}
											</a>
										</div>
										<div className="col-md-12 space-s-t">
											<div className="checkbox">
												<label>
													<input
														{...register("acceptTerms", {
															required: t("mustAcceptTerms")
														})}
														type="checkbox"
													/>
													{t("acceptTerms")}
												</label>
											</div>
											{acceptTermsError && <div className="checkbox-error">{acceptTermsError}</div>}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<NaviButtons
				cancelVisible
				onCancel={() => (window.location.href = returnUrl)}
				backVisible
				onBack={onBack}
				nextDisabled={isSubmitting}
				nextText="confirmOrder"
			/>
		</form>
	);
};
